import { lazy, Suspense, type ReactElement } from "react";
import { LoadingLines, ReaderLoading } from "./Loading";
import { RunListPage, listSearch } from "./WorkspaceLists";
import { InboxPage } from "./InboxPage";
import { AnswerPage } from "./AnswerPage";
import { StaffPage } from "./StaffPage";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Link,
  Outlet,
  type RouterHistory,
} from "@tanstack/react-router";
import {
  Dashboard,
  AccountPage,
  NewWorkflowPage,
  WorkflowDetailPage,
  type DashboardPage,
} from "./Dashboard";
import { MethodsPage } from "./components/dashboard/MethodsPanel";
import { buttonVariants } from "./components/ui/button";

// The run page loads the method editor code only when it opens.
const LazyRunPage = lazy(() => import("./DocumentWorkspace").then(module => ({ default: module.RunPage })));
function RunPage() {
  return <Suspense fallback={<ReaderLoading run />}><LazyRunPage /></Suspense>;
}
const comparisonPage = (name: "ComparisonsPage" | "ComparisonPage" | "ComparisonPointPage" | "ComparisonRunPage") => {
  const Page = lazy(() => import("./ComparisonPages").then(module => ({ default: module[name] as () => ReactElement })));
  // While its code loads, the page's main region already says that it is loading.
  return function ComparisonRoute() {
    return <Suspense fallback={<main className="p-wrap cmp" aria-busy="true"><LoadingLines rows={6} label={name === "ComparisonsPage" ? "Loading the comparisons" : "Loading the comparison"} /></main>}><Page /></Suspense>;
  };
};
const number = (value: unknown) => typeof value === "number" && Number.isInteger(value) && value >= 0 ? value
  : typeof value === "string" && /^\d+$/.test(value) ? Number(value) : undefined;
const LazyProposalPage = lazy(() => import("./ProposalPage").then(module => ({ default: module.ProposalPage })));
function ProposalPage() {
  return <Suspense fallback={<ReaderLoading />}><LazyProposalPage /></Suspense>;
}

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    page?: DashboardPage;
  }
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
function NotFoundPage() {
  return (
    <div className="dash-state">
      <h2>Page not found</h2>
      <p>This link does not open a Method page.</p>
      <Link className={buttonVariants()} to="/">
        Open Methods
      </Link>
    </div>
  );
}
const root = createRootRoute({
  component: Outlet,
  notFoundComponent: NotFoundPage,
});
const dashboard = createRoute({
  getParentRoute: () => root,
  id: "dashboard",
  component: Dashboard,
  notFoundComponent: NotFoundPage,
});
const home = createRoute({
  getParentRoute: () => dashboard,
  path: "/",
  staticData: { page: "workflows" },
  component: MethodsPage,
});
const newWorkflow = createRoute({
  getParentRoute: () => dashboard,
  path: "/new",
  staticData: { page: "new" },
  component: NewWorkflowPage,
});
const workflows = createRoute({
  getParentRoute: () => dashboard,
  path: "/methods",
  staticData: { page: "workflows" },
  component: MethodsPage,
});
const detail = createRoute({
  getParentRoute: () => dashboard,
  path: "/methods/$workflowId",
  validateSearch: (search:Record<string,unknown>): {version?:string; compare?:string} => ({ ...(typeof search.version === "string" ? {version:search.version} : {}), ...(typeof search.compare === "string" ? {compare:search.compare} : {}) }),
  staticData: { page: "detail" },
  component: WorkflowDetailPage,
});
const proposal = createRoute({
  getParentRoute: () => dashboard,
  path: "/methods/$workflowId/proposals/$proposalId",
  staticData: { page: "detail" },
  component: ProposalPage,
});
const runs = createRoute({ getParentRoute: () => dashboard, path: '/runs', validateSearch: listSearch, staticData: {page:'runs'}, component:RunListPage });
const runDetail = createRoute({ getParentRoute: () => dashboard, path: '/runs/$runId', staticData: {page:'run'}, component:RunPage });
// Comparisons: the list, a comparison's result page (?version=N for an older version), one point's runs, and one run.
const comparisons = createRoute({ getParentRoute: () => dashboard, path: "/comparisons", staticData: { page: "comparisons" }, component: comparisonPage("ComparisonsPage") });
// The version is a number, so its link reads ?version=2 (a text would read ?version=%222%22).
const comparisonSearch = (search: Record<string, unknown>): { version?: number } => { const version = number(search.version); return version ? { version } : {}; };
const comparison = createRoute({ getParentRoute: () => dashboard, path: "/comparisons/$comparisonId", validateSearch: comparisonSearch,
  staticData: { page: "comparison" }, component: comparisonPage("ComparisonPage") });
const comparisonPoint = createRoute({ getParentRoute: () => dashboard, path: "/comparisons/$comparisonId/point",
  validateSearch: (search: Record<string, unknown>): { version?: number; arm?: string; at?: number } => {
    const at = number(search.at);
    return { ...comparisonSearch(search), ...(typeof search.arm === "string" ? { arm: search.arm } : {}), ...(at !== undefined ? { at } : {}) };
  },
  staticData: { page: "comparison" }, component: comparisonPage("ComparisonPointPage") });
const comparisonRun = createRoute({ getParentRoute: () => dashboard, path: "/comparisons/$comparisonId/runs/$runId", staticData: { page: "comparison" }, component: comparisonPage("ComparisonRunPage") });
const inbox = createRoute({ getParentRoute: () => dashboard, path: "/inbox", staticData: { page: "inbox" }, component: InboxPage });
// A one-time answer link: outside the dashboard, so it needs no sign-in.
const answer = createRoute({ getParentRoute: () => root, path: "/answer/$token", component: AnswerPage });
const settings = createRoute({
  getParentRoute: () => dashboard,
  path: "/settings",
  staticData: { page: "settings" },
  component: AccountPage,
});
// God Mode: staff only. The page itself and the API both refuse anyone else.
const staff = createRoute({ getParentRoute: () => dashboard, path: "/staff", staticData: { page: "staff" }, component: StaffPage });
const routeTree = root.addChildren([
  dashboard.addChildren([
    home,
    newWorkflow,
    workflows,
    detail,
    proposal,
    runs,
    runDetail,
    inbox,
    comparisons,
    comparison,
    comparisonPoint,
    comparisonRun,
    settings,
    staff,
  ]),
  answer,
]);
export function createAppRouter(history?: RouterHistory) {
  return createRouter({
    routeTree,
    history,
    defaultPreload: "intent",
    defaultPendingMs: 150,
    trailingSlash: "never",
  });
}
